import * as yup from "yup";
import * as styles from "./SectionBehaviourHelplineForm.module.scss";

import { navigate } from "gatsby";
import React, { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
	Button,
	Form,
	FormSelect,
	FormTextArea,
	FormTextInput,
} from "../../../components/common";
import type { SelectedDropdownItem } from "../../../components/common/FormSelect/FormSelect";
import { Colours } from "../../../utils/colours";
import { handleTrailingSlash } from "../../../utils/string";
import WrappedSection from "../../WrappedSection/WrappedSection";

interface BehaviourFormFields {
	recaptchaToken: string;
	behaviourFormDogName: string;
	behaviourFormDogBreed: string;
	behaviourFormDogAge: string;
	behaviourFormDogSex: SelectedDropdownItem;
	behaviourFormFirstName: string;
	behaviourFormLastName: string;
	behaviourFormHouseNumberName: string;
	behaviourFormPostcode: string;
	behaviourFormPhone: string;
	behaviourFormCallbackTime: string;
	behaviourFormEmail: string;
	behaviourFormMessage: string;
}

const sexOptions = [
	{ label: "Male", value: "male" },
	{ label: "Female", value: "female" },
];

export interface SectionBehaviourHelplineFormProps {
	heading: string;
	htmlIntroText?: string;
}

export const getSectionBehaviourHelplineFormProps = ({
	field_front_end_title,
	field_section_short_description,
}: Queries.SectionDataFragment): SectionBehaviourHelplineFormProps => ({
	heading: field_front_end_title,
	htmlIntroText: field_section_short_description?.value,
});

export const SectionBehaviourHelplineForm: React.FC<
	SectionBehaviourHelplineFormProps
> = () => {
	const { executeRecaptcha } = useGoogleReCaptcha();

	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const submitHandler = async (formData: BehaviourFormFields) => {
		setIsLoading(true);
		setIsSubmitted(true);

		const recaptchaToken = await executeRecaptcha?.("behaviour_form");
		await fetch(process.env.GATSBY_BEHAVIOUR_HELPLINE_API_URL, {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				...formData,
				recaptchaToken,
				behaviourHelpLineFormSubject: "Behaviour Support Line Enquiry",
			}),
		})
			.then((res) => {
				setIsLoading(false);
				navigate(
					handleTrailingSlash(
						`${window.location.href}/${res.ok ? "thank-you" : "error"}`,
					),
				);
			})
			.catch((_err) => {
				setIsLoading(false);
				navigate(handleTrailingSlash(`${window.location.href}/${"error"}`));
			});
	};
	return (
		<Form
			className={styles.behaviourform}
			onSubmitFn={submitHandler}
			validationSchema={{
				behaviourFormDogName: yup
					.string()
					.required("Please enter your Dogs name")
					.matches(
						/^[A-zÀ-ÿ\-\u0621-\u064A\u0401\u0451\u0410-\u044f\' ]*$/,
						"Must only be characters A-Z and international characters",
					)
					.min(1, "Please enter your Dogs name")
					.max(40, "Please enter a name less than 40 characters"),
				behaviourFormDogBreed: yup
					.string()
					.required("Please enter the breed of your dog"),
				behaviourFormDogAge: yup
					.string()
					.required("Please enter the age of your dog")
					.matches(/^[0-9\+]+$/, "Must be only digits")
					.min(1, "Must be a minimum of 1 digit"),
				behaviourFormDogSex: yup
					.object()
					.required("Please choose the sex of your dog"),
				behaviourFormFirstName: yup
					.string()
					.required("Please enter your first name")
					.matches(
						/^[A-zÀ-ÿ\-\u0621-\u064A\u0401\u0451\u0410-\u044f\' ]*$/,
						"Must only be characters A-Z and international characters",
					)
					.min(1, "Please enter your first name")
					.max(40, "Please enter a first name less than 40 characters"),
				behaviourFormLastName: yup
					.string()
					.required("Please enter your last name")
					.matches(
						/^[A-zÀ-ÿ\-\u0621-\u064A\u0401\u0451\u0410-\u044f\' ]*$/,
						"Must only be characters A-Z and international characters",
					)
					.min(1, "Please enter your last name")
					.max(40, "Please enter a last name less than 40 characters"),
				behaviourFormHouseNumberName: yup
					.string()
					.required("Please enter your house number / name"),
				behaviourFormPostcode: yup
					.string()
					.min(5, "Please enter your postcode")
					.required("Please enter your postcode"),
				behaviourFormPhone: yup
					.string()
					.required("Please enter your phone number")
					.matches(/^[0-9\s]*$/, "Must be only digits")
					.min(11, "Must be a minimum of 11 digits"),
				behaviourFormCallbackTime: yup.string().required("Please enter a time"),
				behaviourFormEmail: yup
					.string()
					.trim()
					.email("That doesn't look like a valid email")
					.required("Please enter your email address"),
				behaviourFormMessage: yup
					.string()
					.required("Please enter your message")
					.min(10, "Your message must be at least 10 characters"),
			}}
		>
			<WrappedSection
				sectionName="SectionBehaviourHelplineFormTop"
				sectionColour={Colours.SANDY_FUR}
			>
				<fieldset className={styles.fieldset}>
					<legend className={styles.legend}>About your dog</legend>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="Dog name"
							placeholder="Name"
							name="behaviourFormDogName"
						/>
					</div>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="Dog breed"
							placeholder="Breed"
							name="behaviourFormDogBreed"
						/>
					</div>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="Dog age (Years)"
							placeholder="Age"
							name="behaviourFormDogAge"
						/>
					</div>
					<div className={styles.inputwrapper}>
						<label
							htmlFor="behaviourFormDogSex"
							className={styles.textinputlabel}
						>
							Dog sex
						</label>
						<FormSelect
							className={styles.select}
							name="behaviourFormDogSex"
							label="Dog sex"
							placeholder="Sex"
							options={sexOptions}
						/>
					</div>
				</fieldset>
			</WrappedSection>

			<WrappedSection sectionName="SectionBehaviourHelplineFormBottom">
				<fieldset className={styles.fieldset}>
					<legend className={styles.legend}>About yourself</legend>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="First name"
							placeholder="First name"
							name="behaviourFormFirstName"
							autoComplete="given-name"
						/>
					</div>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="Last name"
							placeholder="Last name"
							name="behaviourFormLastName"
							autoComplete="family-name"
						/>
					</div>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="House number / name"
							placeholder="House number / name"
							name="behaviourFormHouseNumberName"
							autoComplete="address-line1"
						/>
					</div>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="Postcode"
							placeholder="Postcode"
							name="behaviourFormPostcode"
							autoComplete="postal-code"
						/>
					</div>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="Phone number"
							placeholder="Phone number"
							name="behaviourFormPhone"
							maxLength={20}
							autoComplete="tel"
						/>
					</div>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="Preferred callback time (9.30am - 5pm, Mon - Sat)"
							placeholder="Preferred callback time"
							name="behaviourFormCallbackTime"
						/>
					</div>
					<div className={styles.inputwrapper}>
						<FormTextArea
							className={styles.inputtextarea}
							label="Why are you seeking support?"
							name="behaviourFormMessage"
							placeholder="Type..."
						/>
					</div>
					<div className={styles.inputwrapper}>
						<FormTextInput
							className={styles.input}
							label="Email address"
							placeholder="Email"
							name="behaviourFormEmail"
							errorMessage="That doesn't look like a valid email"
							type="email"
							autoComplete="email"
						/>
					</div>
					<div className={styles.submitcontainer}>
						<Button
							variant="primary"
							type="submit"
							ariaLabel="submit"
							fullWidth={true}
							onClick={() => {}}
							isLoading={isLoading}
							disabled={isSubmitted}
						>
							Submit
						</Button>
					</div>
				</fieldset>
			</WrappedSection>
		</Form>
	);
};

export default SectionBehaviourHelplineForm;
